// libs
import React, { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { LargeAddBlock } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
// components
import { Box, Typography } from '@lyearn/ui';
import Warnings from '@/components/Form/components/Warning';
import { useAppConfig } from '@/hooks/useAppConfig';
import { CertificateSettingsInput, ResourceTypeEnum } from '@/types/schema';

import { getDefaultCertificateSettings } from '../../consts';
// consts
// hooks
import useCertificateTemplate from '../../hooks/useCertificateTemplate';
// types
import { CertificateDialogs } from '../../types';
// components
import AttachCertificateDialog from '../AttachCertificateDialog';
import CertificateCard from '../CertificateCard';
import CertificateSettings from '../CertificateSettings';
import DeleteCertificateDialog from '../DeleteCertificateDialog';

export type AddCertificateProps = {
  certificateSettings: CertificateSettingsInput;
  className?: string;
  footer?: string;
  header?: {
    description?: string;
    showDescription?: boolean;
    title?: string;
  };
  onChange: (certificateSettings?: CertificateSettingsInput) => void;
  contentType?: ResourceTypeEnum | 'EVENT';
  error?: boolean;
  showConditionSettings?: boolean;
  warning?: string;
  readonly?: boolean;
};

function AddCertificate({
  certificateSettings,
  className,
  footer,
  header = {},
  onChange,
  contentType,
  error,
  showConditionSettings = true,
  warning,
  readonly,
}: AddCertificateProps) {
  const { t } = useTranslation('Certificate');
  const {
    description = t('Certificate:Form.CertificateDescription'),
    showDescription = true,
    title = t('Certificate:Form.Certificate'),
  } = header;
  const { site } = useAppConfig();
  const { disableLinkedInSharing, disableSocialSharing } = site.certificateSharingSettings;

  const [inViewDialog, setInViewDialog] = useState<CertificateDialogs>();
  const { certificateTemplate, fetching } = useCertificateTemplate({
    certificateId: certificateSettings?.certificateTemplateId,
  });

  const onClose = () => setInViewDialog(undefined);

  const _onChange = useCallback(
    (settings?: CertificateSettingsInput) => {
      onChange({ ...certificateSettings, ...settings });
    },
    [certificateSettings, onChange],
  );

  const onAttach = useCallback(
    (certificateId: string) => {
      onChange({ ...certificateSettings, certificateTemplateId: certificateId });
      onClose();
    },
    [certificateSettings, onChange],
  );

  const onDelete = useCallback(() => {
    onChange(getDefaultCertificateSettings({ disableLinkedInSharing, disableSocialSharing }));
    onClose();
  }, [disableLinkedInSharing, disableSocialSharing, onChange]);

  return (
    <>
      <Box
        className={cn('flex flex-col', className)}
        style={readonly ? { pointerEvents: 'none' } : {}}>
        <Box className="mb-24">
          <Typography className="ps-8" variant="heading-s">
            {title}
          </Typography>
          {showDescription ? (
            <Typography className="mt-8 ps-8" color="text-text-secondary" variant="body-short-01">
              {description}
            </Typography>
          ) : null}
        </Box>
        {!certificateSettings?.certificateTemplateId ? (
          <LargeAddBlock
            subTitle={t('Certificate:Form.AddCertificateDescription')}
            title={t('Certificate:Form.AddCertificate')}
            onClick={() => setInViewDialog(CertificateDialogs.SelectTemplate)}
          />
        ) : certificateTemplate ? (
          <>
            <CertificateCard
              certificate={certificateTemplate!}
              onDelete={() => setInViewDialog(CertificateDialogs.DeleteCertificate)}
              onReplace={() => setInViewDialog(CertificateDialogs.SelectTemplate)}
            />
            <CertificateSettings
              certificateSettings={certificateSettings}
              contentType={contentType}
              error={error}
              showConditionSettings={showConditionSettings}
              onChange={_onChange}
            />
          </>
        ) : null}
        {footer ? (
          <Typography className="mt-16" color="text-text-secondary" variant="body-short-02">
            {footer}
          </Typography>
        ) : null}
        <Warnings className="mt-32" warnings={warning ? [warning] : undefined} />
      </Box>
      {inViewDialog === CertificateDialogs.SelectTemplate ? (
        <AttachCertificateDialog
          selectedCertificateId={certificateSettings?.certificateTemplateId}
          onAttach={onAttach}
          onClose={onClose}
        />
      ) : null}
      {inViewDialog === CertificateDialogs.DeleteCertificate ? (
        <DeleteCertificateDialog
          certificateTemplate={certificateTemplate}
          onClose={onClose}
          onDelete={onDelete}
        />
      ) : null}
    </>
  );
}

export default React.memo(AddCertificate);
